import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSchemaIntegrationData } from "../hooks/useSchemaIntegrationData";
import type { ButtonComponentProps } from "../types";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import type { ButtonVariant } from "@/features/integrations/types";

const BUTTON_VARIANT_MAP: Record<
  ButtonVariant,
  "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
> = {
  Default: "default",
  Destructive: "destructive",
  Outline: "outline",
  Secondary: "secondary",
  Ghost: "ghost",
  Link: "link",
};

/**
 * ButtonComponent - renders a button that triggers an action.
 * On click calls executeAction(actionKey, messages).
 * Fetches integration data to support visibility checks via visibleWhenDataKey.
 */
function ButtonComponent({
  label,
  variant = "Default",
  action,
  executeAction,
  data,
  visibleWhenDataKey,
  icon,
  integrationName,
  moduleName,
  clientId,
  tenantId,
}: ButtonComponentProps) {
  const { t } = useTranslation();

  // Fetch integration data to support visibility checks
  const moduleIntegrationDataResult = useSchemaIntegrationData(
    integrationName,
    moduleName ?? "",
    clientId,
    tenantId
  );

  // Merge context data with module-specific data for visibility checks
  const mergedData = useMemo(() => {
    return {
      ...data,
      ...moduleIntegrationDataResult.data,
    };
  }, [data, moduleIntegrationDataResult.data]);

  if (visibleWhenDataKey) {
    const isVisible = Boolean(mergedData?.[visibleWhenDataKey]);
    if (!isVisible) {
      return null;
    }
  }

  const displayLabel =
    label != null && String(label).trim() !== ""
      ? t(label)
      : mergedData != null && "text" in mergedData
        ? t(String(mergedData.text ?? ""), { defaultValue: String(mergedData.text ?? "") })
        : "";

  if (!displayLabel) {
    return null;
  }

  const handleClick = () => {
    if (action) {
      executeAction(action.actionKey, {
        successMessage: action.successMessage,
        errorMessage: action.errorMessage,
      });
    }
  };

  const variantKey = BUTTON_VARIANT_MAP[variant] ?? "outline";

  // Map icon name to icon component
  const IconComponent = icon === "sync" ? Icons.SyncIcon : null;

  return (
    <Button variant={variantKey} size="sm" type="button" onClick={handleClick}>
      {IconComponent && (
        <IconComponent fontSize={16} className="mr-1.5" />
      )}
      {displayLabel}
    </Button>
  );
}

export { ButtonComponent };
